import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { TaskBoard } from '../components/TaskBoard.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { ROLES } from '../utils/roles.js';

export function AdminTasksPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const canSeeAll = user?.role === ROLES.ADMIN || user?.role === ROLES.MANAGER;
  const mineOnly = !canSeeAll || searchParams.get('mine') === '1' || (searchParams.get('mine') !== '0' && user?.role === ROLES.TEAM_MEMBER);
  const [queueFilters, setQueueFilters] = useState({ quick: '', client_id: '', project_id: '', assigned_to: '', priority: '' });
  const [ready, setReady] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState('');

  const canDeleteTask = (task) =>
    Boolean(user?.capabilities?.company_admin) || Number(task.created_by) === Number(user?.id);

  const load = useCallback(async () => {
    setError('');
    try {
      const params = new URLSearchParams();
      if (mineOnly) params.set('mine', '1');
      const q = (searchParams.get('q') || '').trim();
      if (q) params.set('q', q);
      if (queueFilters.quick) params.set('quick', queueFilters.quick);
      if (queueFilters.client_id) params.set('client_id', queueFilters.client_id);
      if (queueFilters.project_id) params.set('project_id', queueFilters.project_id);
      if (queueFilters.assigned_to) params.set('assigned_to', queueFilters.assigned_to);
      if (queueFilters.priority) params.set('priority', queueFilters.priority);
      const qs = params.toString();
      const [taskList, projectList, staff, stages] = await Promise.all([
        apiJson(`/api/admin/tasks${qs ? `?${qs}` : ''}`),
        apiJson('/api/admin/projects'),
        apiJson('/api/admin/tasks/assignees'),
        apiJson('/api/admin/task-columns'),
      ]);
      setTasks(taskList);
      setProjects(projectList);
      setAssignees(staff);
      setColumns(stages.columns || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setReady(true);
    }
  }, [mineOnly, queueFilters, searchParams]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onRefresh = () => { void load(); };
    window.addEventListener('staff-task-refresh', onRefresh);
    return () => window.removeEventListener('staff-task-refresh', onRefresh);
  }, [load]);

  const tasksHref = (mine) => {
    const params = new URLSearchParams(searchParams);
    if (mine === '1') params.set('mine', '1');
    else if (user?.role === ROLES.TEAM_MEMBER) params.set('mine', '0');
    else params.delete('mine');
    const qs = params.toString();
    return qs ? `/admin/tasks?${qs}` : '/admin/tasks';
  };

  const visibleTasks = tasks;

  return (
    <div>
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
          {mineOnly ? 'My Tasks' : 'Tasks'}
        </h2>
      </div>
      {canSeeAll ? (
        <p className="row-actions">
          <Link to={tasksHref('0')} className={!mineOnly ? 'btn-link' : 'muted small'}>
            All tasks
          </Link>
          <Link to={tasksHref('1')} className={mineOnly ? 'btn-link' : 'muted small'}>
            My tasks
          </Link>
        </p>
      ) : null}
      <p className="muted small">My tasks opens first for team members. Use the list for what’s due, or the board to move work between To Do, In Progress, Waiting, and Complete.</p>
      {error ? <p className="err">{error}</p> : null}
      {!ready ? <p className="muted">Loading tasks…</p> : (
        <TaskBoard
          tasks={visibleTasks}
          onChange={(next) => {
            const q = (searchParams.get('q') || '').trim();
            if (!q) {
              setTasks(next);
              return;
            }
            const visibleIds = new Set(visibleTasks.map((task) => task.id));
            const nextIds = new Set(next.map((task) => task.id));
            const removed = new Set([...visibleIds].filter((id) => !nextIds.has(id)));
            const nextById = new Map(next.map((task) => [task.id, task]));
            const kept = tasks
              .filter((task) => !removed.has(task.id))
              .map((task) => nextById.get(task.id) || task);
            const keptIds = new Set(kept.map((task) => task.id));
            setTasks([...kept, ...next.filter((task) => !keptIds.has(task.id))]);
          }}
          projects={projects}
          assignees={assignees}
          canDelete={canDeleteTask}
          showProject
          columns={columns}
          stageScope="agency"
          initialTaskId={searchParams.get('task')}
          onFiltersChange={(next) => {
            setQueueFilters((current) => (
              current.quick === next.quick
              && current.client_id === next.client_id
              && current.project_id === next.project_id
              && current.assigned_to === next.assigned_to
              && current.priority === next.priority
                ? current
                : next
            ));
          }}
          onColumnsChange={setColumns}
          onError={setError}
        />
      )}
    </div>
  );
}
